/**
 * ECM / SDOH / Care-plan insights — sources data from `public.*` (live Trust
 * Marketplace schema), not legacy_trust. Adoption is currently early
 * (small N for SDOH + care plans); the panel renders even when data is thin,
 * with clear empty states that explain what each card will show as adoption grows.
 */

import React from 'react';
import { HeartPulse, ShieldAlert, FileHeart, Layers } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useProgramEnrollmentFunnel,
  useSDOHCoverage,
  useCarePlanCoverage,
  useAtRiskWithoutPlan,
} from '@/hooks/ml';
import { CollapsibleCard } from '@/components/communications/insights/CollapsibleCard';

function fmtPct(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(1)}%`;
}

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon?: React.ReactNode;
}> = ({ label, value, hint, icon }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3 flex items-start gap-3">
    {icon && <div className="text-indigo-600">{icon}</div>}
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
      {hint && <div className="text-xs text-gray-500">{hint}</div>}
    </div>
  </div>
);

const ProgramEnrollmentCard: React.FC = () => {
  const { data, isLoading } = useProgramEnrollmentFunnel();
  const rows = data?.programs ?? [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <Layers className="h-4 w-4 text-indigo-600" />
          Program enrollment funnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500">
            No program enrollments yet. As CBOs start enrolling members via the
            Trust Marketplace, this card will show active / pending / completed
            counts per program.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[28rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Program</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3 text-right">Active</th>
                  <th className="py-2 pr-3 text-right">Pending</th>
                  <th className="py-2 pr-3 text-right">Completed</th>
                  <th className="py-2 pr-3 text-right">Members</th>
                  <th className="py-2 pr-3 text-right">CBOs</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.program_id ?? `unk-${i}`}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-1.5 pr-3 font-medium text-gray-900">
                      {r.program_name || `#${r.program_id ?? '?'}`}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.program_type || '—'}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.active.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.pending.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.completed.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.unique_members.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.cbos_enrolling.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SDOHCoverageCard: React.FC = () => {
  const { data, isLoading } = useSDOHCoverage();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <ShieldAlert className="h-4 w-4 text-rose-600" />
          SDOH screening coverage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : !data ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Members" value={data.total_members.toLocaleString()} />
              <Stat label="Assessed" value={data.assessed_members.toLocaleString()} />
              <Stat label="Coverage" value={fmtPct(data.coverage_pct)} hint="of members with ≥1 SDOH assessment" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">
                Risk distribution
              </div>
              {data.risk_distribution.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No assessments yet. Once members complete PRAPARE / AHC-HRSN
                  / employer screenings, the distribution shows
                  low/moderate/high/critical counts here.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.risk_distribution.map((r) => (
                    <span
                      key={r.risk_level}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                    >
                      <span className="font-medium">{r.risk_level}</span>
                      <span className="tabular-nums text-gray-600">{r.n}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CarePlanCoverageCard: React.FC = () => {
  const { data, isLoading } = useCarePlanCoverage();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <FileHeart className="h-4 w-4 text-emerald-600" />
          Care plan coverage
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : !data ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Members" value={data.total_members.toLocaleString()} />
            <Stat
              label="With care plan"
              value={data.members_with_care_plan.toLocaleString()}
            />
            <Stat label="Coverage" value={fmtPct(data.coverage_pct)} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AtRiskNoPlanCard: React.FC = () => {
  const { data, isLoading } = useAtRiskWithoutPlan();
  const m = data?.metrics;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <HeartPulse className="h-4 w-4 text-amber-600" />
          Action queue — high SDOH risk without follow-up
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : !m ? (
          <div className="text-sm text-gray-500">
            No high/critical SDOH-risk members identified yet. As SDOH assessments
            accumulate, members flagged high or critical who lack a care plan or
            active program enrollment will surface here as an actionable list.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Stat
              label="At-risk members"
              value={m.members_at_risk.toLocaleString()}
              hint="SDOH risk = high or critical"
            />
            <Stat
              label="No care plan"
              value={m.without_care_plan.toLocaleString()}
              hint="needs follow-up"
            />
            <Stat
              label="No active enrollment"
              value={m.without_program_enrollment.toLocaleString()}
              hint="prioritize for outreach"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ECMInsightsPanel: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
      <HeartPulse className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-medium">ECM / SDOH / care plan insights</div>
        <div className="text-indigo-800/90">
          Sources data from the live Trust Marketplace schema (SDOH assessments,
          care plans, program enrollments). Adoption is early — cards render
          even when data is thin so you can see the shape that grows with usage.
        </div>
      </div>
    </div>
    <CollapsibleCard id="ecm-sdoh-coverage" title="SDOH screening coverage" defaultOpen>
      <SDOHCoverageCard />
    </CollapsibleCard>
    <CollapsibleCard id="ecm-care-plan-coverage" title="Care plan coverage">
      <CarePlanCoverageCard />
    </CollapsibleCard>
    <CollapsibleCard id="ecm-at-risk" title="Action queue — high SDOH risk without follow-up">
      <AtRiskNoPlanCard />
    </CollapsibleCard>
    <CollapsibleCard id="ecm-program-enrollment" title="Program enrollment funnel">
      <ProgramEnrollmentCard />
    </CollapsibleCard>
  </div>
);

export default ECMInsightsPanel;
